(() => {
  'use strict';
  if (window.__slickLandingLoaded) return;
  window.__slickLandingLoaded = true;
  const controllers = new Map();

  function initialize(root) {
    if (controllers.has(root)) return;
    const abort = new AbortController();
    const observers = [];
    const on = (el, type, handler, options = {}) => el?.addEventListener(type, handler, { ...options, signal: abort.signal });
    const one = selector => root.querySelector(selector);
    const all = selector => [...root.querySelectorAll(selector)];
    const labels = {
      add: root.dataset.labelAdd ?? 'Add bundle to cart',
      soldOut: root.dataset.labelSoldOut ?? 'Sold out',
      unavailable: root.dataset.labelUnavailable ?? 'Unavailable',
      adding: root.dataset.labelAdding ?? 'Adding bundle…',
      checkCart: root.dataset.labelCheckCart ?? 'Check your cart'
    };
    const messages = {
      adding: root.dataset.messageAdding ?? 'Adding your bundle…',
      added: root.dataset.messageAdded ?? 'Your bundle is in the cart.',
      addError: root.dataset.messageAddError ?? 'The bundle could not be added. Please try again.',
      addUncertain: root.dataset.messageAddUncertain ?? 'We couldn’t confirm the add. Check your cart before trying again so you don’t add the bundle twice.',
      photoLoading: root.dataset.messagePhotoLoading ?? 'Loading photo…',
      photoError: root.dataset.messagePhotoError ?? 'Photo could not load. Please choose another photo.'
    };
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const animate = (element, frames, duration) => {
      if (!reduceMotion.matches && !document.body.hasAttribute('data-disable-animations') && element.animate) element.animate(frames, { duration, easing: 'ease-out' });
    };

    const slides = all('[data-slick-slide]');
    const thumbs = all('[data-slick-thumb]');
    const slideRegion = one('[data-slick-slides]');
    const caption = one('[data-slick-caption]');
    const galleryStatus = one('[data-slick-gallery-status]');
    const galleryFeedback = one('[data-slick-gallery-feedback]');
    const dialog = one('[data-slick-dialog]');
    const dialogImage = one('[data-slick-dialog-image]');
    const dialogCaption = one('[data-slick-dialog-caption]');
    let active = 0;
    let requested = active;
    let galleryRequest = 0;
    let suppressOpenUntil = 0;
    let dialogOpener;
    const thumbRegion = one('[data-slick-thumbs]');
    if (thumbRegion) thumbRegion.hidden = slides.length < 2;

    const photoCaption = (index, image) => `${index + 1} / ${slides.length} · ${image.alt}`;
    function syncDialogPhoto() {
      if (!dialog?.open || !slides.length) return;
      const source = slides[active].querySelector('img');
      dialogImage.src = source.dataset.zoomSrc || source.currentSrc || source.src;
      dialogImage.alt = source.alt;
      dialogCaption.textContent = photoCaption(active, source);
    }
    async function showPhoto(index) {
      if (!slides.length || abort.signal.aborted) return;
      const next = ((index % slides.length) + slides.length) % slides.length;
      // Navigation builds on the latest request even while another image decodes.
      requested = next;
      const token = ++galleryRequest;
      const image = slides[next].querySelector('img');
      // Hidden alternate images are requested only when a visitor chooses them.
      image.loading = 'eager';
      galleryStatus.textContent = messages.photoLoading;
      galleryFeedback.textContent = messages.photoLoading;
      galleryFeedback.hidden = false;
      if (dialog?.open) dialogCaption.textContent = messages.photoLoading;
      try { if (image.decode) await image.decode(); } catch (_) {
        if (token === galleryRequest && !abort.signal.aborted) {
          galleryStatus.textContent = messages.photoError;
          galleryFeedback.textContent = galleryStatus.textContent;
          if (dialog?.open) dialogCaption.textContent = messages.photoError;
        }
        return;
      }
      if (token !== galleryRequest || abort.signal.aborted) return;
      active = next;
      galleryFeedback.hidden = true;
      slides.forEach((slide, i) => { slide.hidden = i !== next; });
      thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === next)));
      caption.textContent = photoCaption(next, image);
      galleryStatus.textContent = `Photo ${next + 1} of ${slides.length}: ${image.alt}`;
      syncDialogPhoto();
      animate(slides[next], [{ opacity: .65 }, { opacity: 1 }], 160);
    }
    const suppressSwipeClick = event => {
      if (event.detail === 0 || performance.now() >= suppressOpenUntil) return false;
      event.preventDefault();
      return true;
    };
    for (const [selector, direction] of [['[data-slick-prev]', -1], ['[data-slick-next]', 1]]) {
      all(selector).forEach(button => {
        button.hidden = slides.length < 2;
        on(button, 'click', event => {
          if (!suppressSwipeClick(event)) showPhoto(requested + direction);
        });
      });
    }
    const galleryKeydown = event => {
      if (slides.length < 2 || event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      // Thumbnails own their arrow/Home/End behavior and focus movement.
      if (event.target.closest?.('[data-slick-thumb], input, textarea, select, [contenteditable="true"]')) return;
      const direction = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
      if (!direction) return;
      event.preventDefault();
      event.stopPropagation();
      showPhoto(requested + direction);
    };
    on(slideRegion, 'keydown', galleryKeydown);
    on(dialog, 'keydown', galleryKeydown);
    thumbs.forEach((thumb, index) => {
      on(thumb, 'click', () => showPhoto(index));
      on(thumb, 'keydown', event => {
        let next;
        if (event.key === 'ArrowRight') next = (index + 1) % thumbs.length;
        if (event.key === 'ArrowLeft') next = (index - 1 + thumbs.length) % thumbs.length;
        if (event.key === 'Home') next = 0;
        if (event.key === 'End') next = thumbs.length - 1;
        if (next === undefined) return;
        event.preventDefault();
        thumbs[next].focus();
        showPhoto(next);
      });
    });
    let touchStart;
    on(slideRegion, 'touchstart', event => {
      suppressOpenUntil = 0;
      touchStart = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { passive: true });
    on(slideRegion, 'touchend', event => {
      if (!touchStart || !event.changedTouches.length) return;
      const x = event.changedTouches[0].clientX - touchStart.x;
      const y = event.changedTouches[0].clientY - touchStart.y;
      if (Math.abs(x) > 45 && Math.abs(x) > Math.abs(y) * 1.5) {
        // The image opener sits over the slide, so ignore the tap synthesized
        // after a swipe without blocking keyboard activation or the next tap.
        suppressOpenUntil = performance.now() + 750;
        showPhoto(requested + (x < 0 ? 1 : -1));
      }
      touchStart = null;
    }, { passive: true });
    on(slideRegion, 'touchcancel', () => { touchStart = null; }, { passive: true });

    if (dialog?.showModal && slides.length) {
      all('[data-slick-open], [data-slick-zoom]').forEach(opener => {
        opener.hidden = false;
        on(opener, 'click', event => {
          if (suppressSwipeClick(event)) return;
          dialogOpener = event.currentTarget;
          if (!dialog.open) dialog.showModal();
          syncDialogPhoto();
        });
      });
      on(one('[data-slick-dialog-close]'), 'click', () => dialog.close());
      on(dialog, 'click', event => { if (event.target === dialog) dialog.close(); });
      on(dialog, 'close', () => {
        if (dialogOpener?.isConnected) dialogOpener.focus({ preventScroll: true });
      });
    }

    const form = one('[data-slick-form]');
    const primary = one('[data-slick-primary]');
    const sticky = one('[data-slick-sticky]');
    const variant = one('[data-slick-variant]');
    const status = one('[data-slick-status]');
    const viewCart = one('[data-slick-view-cart]');
    const buttons = all('[data-slick-submit]');
    let pending = false;
    let uncertain = false;
    let available = form && !primary.disabled;
    let unavailable = variant?.selectedOptions[0]?.dataset.unavailable === 'true';
    let pastPrimary = false;
    let finalVisible = false;
    const syncSticky = () => {
      if (!sticky) return;
      // Do not remove keyboard focus from an active sticky purchase action.
      const show = pastPrimary && !finalVisible && available && !dialog?.open;
      if (!show && sticky.contains(document.activeElement)) return;
      const wasHidden = sticky.hidden;
      sticky.hidden = !show;
      sticky.inert = !show;
      if (show && wasHidden) animate(sticky, [{ transform: 'translateY(8px)', opacity: .8 }, { transform: 'translateY(0)', opacity: 1 }], 180);
    };
    if (form && 'IntersectionObserver' in window) {
      const primaryObserver = new IntersectionObserver(entries => {
        const entry = entries[0];
        pastPrimary = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
        syncSticky();
      }, { threshold: 0 });
      primaryObserver.observe(primary);
      observers.push(primaryObserver);
      const finalObserver = new IntersectionObserver(entries => { finalVisible = entries[0].isIntersecting; syncSticky(); }, { rootMargin: '0px 0px 90px 0px' });
      finalObserver.observe(one('[data-slick-final]'));
      observers.push(finalObserver);
      on(sticky, 'focusout', () => queueMicrotask(syncSticky));
    }

    function setButtons(label) {
      buttons.forEach(button => {
        button.disabled = pending || uncertain || !available;
        button.setAttribute('aria-busy', String(pending));
        button.querySelector('[data-slick-button-label]').textContent = label;
      });
    }
    const idleLabel = () => uncertain ? labels.checkCart : unavailable ? labels.unavailable : available ? labels.add : labels.soldOut;
    const refreshPromotionPrice = () => {
      if (!form) return;
      const selected = variant?.selectedOptions[0];
      const base = Number(selected ? selected.dataset.priceCents : root.dataset.priceCents);
      if (!Number.isFinite(base)) return;
      const price = available && window.BCJPromotion ? window.BCJPromotion.price({
        handle: root.dataset.productHandle, variantId: selected ? selected.value : root.dataset.variantId,
        price: base, sellingPlanId: ''
      }) : base;
      const compare = price < base ? base : Number(selected ? selected.dataset.compareCents : root.dataset.compareCents);
      const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency: window.Shopify?.currency?.active || 'USD'
      }).format(cents / 100);
      all('[data-slick-price]').forEach(node => { node.textContent = money(price); });
      const compareNode = one('[data-slick-compare]');
      if (compareNode) compareNode.hidden = compare <= price;
      const compareValue = one('[data-slick-compare-value]');
      if (compareValue) compareValue.textContent = compare > price ? money(compare) : '';
    };
    on(window, 'bcj:promotion:change', refreshPromotionPrice);
    refreshPromotionPrice();
    on(variant, 'change', () => {
      const selected = variant.selectedOptions[0];
      available = selected.dataset.available === 'true';
      unavailable = selected.dataset.unavailable === 'true';
      refreshPromotionPrice();
      setButtons(idleLabel());
      const url = new URL(location.href);
      url.searchParams.set('variant', selected.value);
      history.replaceState(null, '', url);
      syncSticky();
    });

    on(form, 'submit', async event => {
      // Keep the native Shopify POST when AJAX support is unavailable.
      if (!window.fetch || !window.FormData) return;
      event.preventDefault();
      if (pending || uncertain || !available || !form.reportValidity()) return;
      pending = true;
      const payload = new FormData(form);
      if (variant) variant.disabled = true;
      setButtons(labels.adding);
      status.textContent = messages.adding;
      status.dataset.error = 'false';
      viewCart.hidden = true;
      let confirmed = false;
      let definiteRejection = false;
      const requestAbort = new AbortController();
      const timeout = window.setTimeout(() => requestAbort.abort(), 15000);
      try {
        const response = await fetch(root.dataset.addUrl, { method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: payload, signal: requestAbort.signal });
        const data = await response.json().catch(() => null);
        if (!response.ok) {
          definiteRejection = response.status >= 400 && response.status < 500;
          throw new Error(typeof data?.description === 'string' ? data.description : messages.addError);
        }
        // A confirmed 2xx is enough: do not repeat a mutation because its JSON/app UI failed.
        confirmed = true;
        if (abort.signal.aborted) return;
        status.textContent = messages.added;
        viewCart.hidden = false;
        one('[data-slick-sticky-status]').textContent = messages.added;
        // UpCart is optional. Await any asynchronous failures separately from the add.
        try {
          if (typeof window.upcartOpenCart === 'function') {
            let drawerTimeout;
            try {
              await Promise.race([
                (async () => {
                  if (typeof window.upcartRefreshCart === 'function') await window.upcartRefreshCart();
                  if (!abort.signal.aborted) await window.upcartOpenCart();
                })(),
                new Promise((_, reject) => { drawerTimeout = setTimeout(() => reject(new Error('Cart drawer unavailable')), 2500); })
              ]);
            } finally { clearTimeout(drawerTimeout); }
          } else {
            window.location.assign(root.dataset.cartUrl);
          }
        } catch (_) {
          if (!abort.signal.aborted) window.location.assign(root.dataset.cartUrl);
        }
      } catch (error) {
        if (!confirmed && !abort.signal.aborted) {
          uncertain = !definiteRejection;
          status.dataset.error = 'true';
          status.textContent = uncertain ? messages.addUncertain : error.message;
          viewCart.hidden = !uncertain;
          status.focus();
        }
      } finally {
        clearTimeout(timeout);
        pending = false;
        if (variant) variant.disabled = false;
        setButtons(idleLabel());
      }
    });

    // Reveal the selected FAQ in Shopify's editor, then restore its previous state.
    const editorFaqs = new Map();
    const selectedFaq = event => {
      const faq = event.target.closest?.('details[data-slick-faq]');
      return faq && root.contains(faq) ? faq : null;
    };
    on(root, 'shopify:block:select', event => {
      const faq = selectedFaq(event);
      if (!faq) return;
      if (!editorFaqs.has(faq)) editorFaqs.set(faq, faq.open);
      faq.open = true;
    });
    on(root, 'shopify:block:deselect', event => {
      const faq = selectedFaq(event);
      if (!faq || !editorFaqs.has(faq)) return;
      faq.open = editorFaqs.get(faq);
      editorFaqs.delete(faq);
    });

    controllers.set(root, () => { abort.abort(); observers.forEach(observer => observer.disconnect()); if (dialog?.open) dialog.close(); });
  }

  function mount(scope = document) {
    if (scope.matches?.('[data-slick-landing]')) initialize(scope);
    scope.querySelectorAll('[data-slick-landing]').forEach(initialize);
  }
  mount();
  document.addEventListener('shopify:section:load', event => mount(event.target));
  document.addEventListener('shopify:section:unload', event => {
    for (const [root, cleanup] of controllers) {
      if (event.target.contains(root)) { cleanup(); controllers.delete(root); }
    }
  });
})();
